import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import {
    FileText,
    Briefcase,
    Eye,
    BarChart3,
    TrendingUp,
    ExternalLink,
    Laptop,
    Clock,
    Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminDashboard = () => {
    const { t } = useTranslation();
    const { user, isAdmin, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [demoRequests, setDemoRequests] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && !isAdmin()) {
            navigate('/login');
        }
    }, [authLoading, user, isAdmin, navigate]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                const [statsRes, contactsRes] = await Promise.all([
                    fetchWithSecurity(`${API_URL}users.php?action=stats`).catch(() => null),
                    fetchWithSecurity(`${API_URL}contacts.php`).catch(() => null)
                ]);

                if (statsRes && statsRes.ok) {
                    const data = await statsRes.json();
                    if (data && !data.error) setStats(data);
                }

                if (contactsRes && contactsRes.ok) {
                    const cData = await contactsRes.json();
                    if (Array.isArray(cData)) {
                        const demos = cData.filter(m => {
                            const text = (m.message || '').toLowerCase();
                            return m.request_type === 'demo' || 
                                   m.product || 
                                   text.includes('დემო') || 
                                   text.includes('demo') || 
                                   text.includes('quote') || 
                                   text.includes('შეთავაზებ');
                        });
                        setDemoRequests(demos.length > 0 ? demos : cData.slice(0, 5));
                    }
                }
            } catch (err) {
                console.error('Failed to fetch dashboard data:', err);
            }
            setLoading(false);
        };

        if (user && isAdmin()) {
            fetchDashboardData();
        }
    }, [user, isAdmin]);

    const getRoleLabel = (role) => {
        switch (role) {
            case 'super_admin': return t('roles.super_admin') || 'სუპერ ადმინი';
            case 'partner': return t('roles.partner') || 'ავტორიზებული პარტნიორი';
            case 'blog_manager': return t('roles.blog_manager') || 'კონტენტის მენეჯერი';
            case 'client': return t('roles.client') || 'კლიენტი';
            default: return role;
        }
    };

    if (authLoading || !user) return null;

    return (
        <AdminLayout title={t('admin.dashboard.title') || 'ადმინისტრაციული მართვის ცენტრი'}>


            {/* ── STATS OVERVIEW ── */}
            <div className="row g-4 mb-4">
                <div className="col-lg-8">
                    <div className="shadcn-card h-100">
                        <div className="shadcn-card-header">
                            <div className="shadcn-card-title">
                                <div className="p-2 rounded-3 bg-indigo bg-opacity-10 text-primary">
                                    <TrendingUp size={20} />
                                </div>
                                <div>
                                    <span className="fw-bold">{t('admin.dashboard.currentStats') || 'მიმდინარე სტატისტიკა'}</span>
                                    <span className="d-block text-muted smallest fw-normal">{t('admin.dashboard.generalMetrics') || 'სისტემის ზოგადი მაჩვენებლები'}</span>
                                </div>
                            </div>
                        </div>
                        <div className="row g-3 mt-1">
                            <div className="col-6 col-md-3">
                                <div className="p-3 rounded-3 bg-light text-center">
                                    <div className="fw-bold fs-4 text-primary">{loading ? '…' : (stats?.total_users ?? '—')}</div>
                                    <div className="text-muted small">{t('admin.dashboard.users') || 'მომხმარებელი'}</div>
                                </div>
                            </div>
                            <div className="col-6 col-md-3">
                                <div className="p-3 rounded-3 bg-light text-center">
                                    <div className="fw-bold fs-4 text-info">{loading ? '…' : (stats?.total_blogs ?? '—')}</div>
                                    <div className="text-muted small">{t('admin.dashboard.articles') || 'სტატია'}</div>
                                </div>
                            </div>
                            <div className="col-6 col-md-3">
                                <div className="p-3 rounded-3 bg-light text-center">
                                    <div className="fw-bold fs-4 text-success">{loading ? '…' : (stats?.total_portfolios ?? '—')}</div>
                                    <div className="text-muted small">{t('admin.dashboard.projects') || 'პროექტი'}</div>
                                </div>
                            </div>
                            <div className="col-6 col-md-3">
                                <div className="p-3 rounded-3 bg-light text-center">
                                    <div className="fw-bold fs-4 text-warning">{loading ? '…' : (stats?.total_tickets ?? '—')}</div>
                                    <div className="text-muted small">{t('admin.dashboard.tickets') || 'ბილეთი'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick System Info Card */}
                <div className="col-lg-4">
                    <div className="shadcn-card h-100">
                        <div className="shadcn-card-header">
                            <div className="shadcn-card-title">
                                <div className="p-2 rounded-3 bg-success bg-opacity-10 text-success">
                                    <BarChart3 size={20} />
                                </div>
                                <span>{t('admin.dashboard.quickInfo') || 'სწრაფი ინფო'}</span>
                            </div>
                        </div>
                        <div className="d-flex flex-column gap-3 mt-2">
                            <div className="d-flex justify-content-between align-items-center p-3 rounded-3 bg-light">
                                <span className="text-dark fw-bold small">{t('admin.dashboard.administrator') || 'ადმინისტრატორი'}</span>
                                <span className="fw-bold text-dark">{user.username}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center p-3 rounded-3 bg-light">
                                <span className="text-dark fw-bold small">{t('admin.dashboard.role') || 'როლი'}</span>
                                <span className="shadcn-badge shadcn-badge-primary">{getRoleLabel(user.role)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center p-3 rounded-3 bg-light">
                                <span className="text-dark fw-bold small">{t('admin.dashboard.system') || 'სისტემა'}</span>
                                <span className="shadcn-badge shadcn-badge-success">{t('admin.dashboard.activeStatus') || 'აქტიური'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── TOP STATS OVERVIEW CARDS WITH LINKS ── */}
            {isSuperAdmin() && (
                <div className="row g-4 mb-4">
                    {/* Top Projects */}
                    <div className="col-lg-6">
                        <div className="shadcn-card h-100">
                            <div className="shadcn-card-header">
                                <div className="shadcn-card-title">
                                    <div className="p-2 rounded-3 bg-primary bg-opacity-10 text-primary">
                                        <Briefcase size={20} />
                                    </div>
                                    <span>{t('admin.dashboard.topProjects') || 'ტოპ პროექტები პორტფოლიოდან'}</span>
                                </div>
                                <Link to="/admin/portfolio" className="shadcn-badge shadcn-badge-primary text-decoration-none">
                                    პორტფოლიო <ExternalLink size={12} className="ms-1" />
                                </Link>
                            </div>
                            <div className="d-flex flex-column gap-2">
                                {loading ? (
                                    <div className="text-muted small py-3">იტვირთება...</div>
                                ) : stats?.top_portfolios?.length > 0 ? (
                                    stats.top_portfolios.map((item, i) => (
                                        <Link
                                            key={i}
                                            to={item.id ? `/admin/portfolio/edit/${item.id}` : '/admin/portfolio'}
                                            className="d-flex justify-content-between align-items-center p-3 rounded-3 bg-light hover-effect cursor-pointer text-decoration-none"
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="fw-bold text-primary smallest rounded-circle bg-white shadow-sm p-2" style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    #{i + 1}
                                                </div>
                                                <span className="text-dark fw-bold small">{item.title}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="shadcn-badge shadcn-badge-info">
                                                    <Eye size={12} /> {item.view_count} {t('admin.dashboard.views') || 'ნახვა'}
                                                </span>
                                                <ExternalLink size={14} className="text-muted" />
                                            </div>
                                        </Link>
                                    ))
                                ) : (
                                    <div className="text-muted small py-3">მონაცემები არ არის</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Top Articles */}
                    <div className="col-lg-6">
                        <div className="shadcn-card h-100">
                            <div className="shadcn-card-header">
                                <div className="shadcn-card-title">
                                    <div className="p-2 rounded-3 bg-info bg-opacity-10 text-info">
                                        <FileText size={20} />
                                    </div>
                                    <span>{t('admin.dashboard.topArticles') || 'პოპულარული სტატიები'}</span>
                                </div>
                                <Link to="/admin/blogs" className="shadcn-badge shadcn-badge-info text-decoration-none">
                                    ბლოგი <ExternalLink size={12} className="ms-1" />
                                </Link>
                            </div>
                            <div className="d-flex flex-column gap-2">
                                {loading ? (
                                    <div className="text-muted small py-3">იტვირთება...</div>
                                ) : stats?.top_blogs?.length > 0 ? (
                                    stats.top_blogs.map((item, i) => (
                                        <Link
                                            key={i}
                                            to={item.id ? `/admin/blogs/edit/${item.id}` : '/admin/blogs'}
                                            className="d-flex justify-content-between align-items-center p-3 rounded-3 bg-light hover-effect cursor-pointer text-decoration-none"
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="fw-bold text-info smallest rounded-circle bg-white shadow-sm p-2" style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    #{i + 1}
                                                </div>
                                                <span className="text-dark fw-bold small">{item.title}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="shadcn-badge shadcn-badge-warning">
                                                    <TrendingUp size={12} /> {item.view_count} {t('admin.dashboard.views') || 'ნახვა'}
                                                </span>
                                                <ExternalLink size={14} className="text-muted" />
                                            </div>
                                        </Link>
                                    ))
                                ) : (
                                    <div className="text-muted small py-3">მონაცემები არ არის</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── DEMO & QUOTE REQUESTS SECTION ── */}
            {isSuperAdmin() && (
                <div className="row g-4 mb-4">
                    <div className="col-12">
                        <div className="shadcn-card">
                            <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                                <div className="shadcn-card-title">
                                    <div className="p-2 rounded-3 bg-primary bg-opacity-10 text-primary">
                                        <Laptop size={20} />
                                    </div>
                                    <div>
                                        <span className="fw-bold">დემო ჩვენების & შეთავაზების მოთხოვნები</span>
                                        <span className="d-block text-muted smallest fw-normal">კლიენტების მიერ პროდუქტებზე გაგზავნილი აქტიური მოთხოვნები</span>
                                    </div>
                                </div>
                                <Link to="/admin/demo-requests" className="shadcn-badge shadcn-badge-primary text-decoration-none">
                                    ყველას ნახვა ({demoRequests.length}) <ExternalLink size={12} className="ms-1" />
                                </Link>
                            </div>

                            {demoRequests.length === 0 ? (
                                <div className="p-4 text-center text-muted small">
                                    ახალი დემო მოთხოვნები არ არის.
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-2 p-3">
                                    {demoRequests.slice(0, 5).map((req, i) => {
                                        const prodMatch = req.message && req.message.match(/\[(?:დემო \/ შეთავაზების მოთხოვნა|Demo Request):\s*([^\]]+)\]/i);
                                        const prodName = req.product || (prodMatch ? prodMatch[1] : 'პროდუქტი');

                                        return (
                                            <div 
                                                key={req.id || i}
                                                className="d-flex flex-column flex-md-row justify-content-between align-items-md-center p-3 rounded-3 bg-light gap-2"
                                            >
                                                <div className="d-flex align-items-center gap-3">
                                                    <span className="badge bg-primary text-white p-2 rounded-3">
                                                        📦 {prodName}
                                                    </span>
                                                    <div>
                                                        <div className="fw-bold text-dark small">{req.name}</div>
                                                        <div className="d-flex align-items-center gap-2 smallest text-muted">
                                                            <Mail size={11} /> {req.email}
                                                            {req.phone && <span>• {req.phone}</span>}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="smallest text-muted d-flex align-items-center gap-1">
                                                        <Clock size={12} />
                                                        <span>{new Date(req.created_at).toLocaleDateString()}</span>
                                                    </div>
                                                    <span className={`shadcn-badge ${req.status === 'New' ? 'shadcn-badge-danger' : 'shadcn-badge-success'}`}>
                                                        {req.status === 'New' ? 'ახალი' : req.status}
                                                    </span>
                                                    <Link to="/admin/demo-requests" className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary">
                                                        დეტალები
                                                    </Link>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminDashboard;
